import { ReactNode, ErrorInfo } from 'react'
import { ErrorBoundary as Boundary, FallbackProps } from 'react-error-boundary'
import { ErrorPage, ErrorPageProps } from '../pages/errorPage'
import { captureException, addBreadcrumb } from '../config/sentry'

interface ErrorBoundaryProps extends Pick<ErrorPageProps, 'linkTo'> {
  children: ReactNode
}

/**
 * Error handler that captures errors to Sentry and logs breadcrumbs
 */
const handleError = (error: Error, info: ErrorInfo) => {
  // Add breadcrumb for component error context
  addBreadcrumb({
    message: 'React Error Boundary triggered',
    level: 'error',
    category: 'robcratch',
    data: {
      componentStack: info.componentStack || 'No component stack available',
      errorMessage: error.message,
      errorName: error.name,
      timestamp: new Date().toISOString(),
    },
  })

  // Capture the error to Sentry with additional context
  captureException(error, {
    componentStack: info.componentStack || 'No component stack available',
    errorBoundary: 'main',
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  })

  // Also log to console for development
  console.error('ErrorBoundary caught an error:', error)
  console.error('Component stack:', info.componentStack)
}

/**
 * Custom fallback component that integrates with Sentry
 */
interface ErrorFallbackProps extends FallbackProps {
  linkTo: string
}

const ErrorFallback = ({ error, linkTo }: ErrorFallbackProps) => {
  // Log additional breadcrumb when fallback is rendered
  addBreadcrumb({
    message: 'Error fallback UI rendered',
    level: 'info',
    category: 'ui',
    data: {
      errorMessage: error?.message,
      timestamp: new Date().toISOString(),
    },
  })

  return (
    <ErrorPage
      title="エラー"
      description="ふめいなエラーがはっせいしました"
      linkTo={linkTo as 'タウン' | 'リスト'}
    />
  )
}

export const ErrorBoundary = ({ children, linkTo }: ErrorBoundaryProps) => (
  <Boundary
    FallbackComponent={(props) => (
      <ErrorFallback {...props} linkTo={linkTo || 'タウン'} />
    )}
    onError={handleError}
    onReset={() => {
      // Add breadcrumb when error boundary is reset
      addBreadcrumb({
        message: 'Error boundary reset by user',
        level: 'info',
        category: 'ui',
        data: {
          timestamp: new Date().toISOString(),
          action: 'reset',
        },
      })
    }}
  >
    {children}
  </Boundary>
)
